import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandItem, CommandEmpty, CommandGroup } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
// Phase 23-08: useListMembers removed (members table dropped). Use useListAccounts instead.
import { useListAccounts, useListInventoryPlans } from "@workspace/api-client-react";
import type { Account } from "@workspace/api-client-react";
import { memId } from "@/lib/display-id";
import { cn } from "@/lib/utils";

interface MemberComboboxProps {
  value: number | null;
  onChange: (id: number) => void;
  className?: string;
  autoFocus?: boolean;
  /** Triggered when popover opens; useful for parents to track focus chain */
  onOpenChange?: (open: boolean) => void;
  /** Filter by member category — e.g. "Non_Recurring" for the New Booking form */
  memberCategory?: string;
}

export function MemberCombobox({ value, onChange, className, autoFocus, onOpenChange, memberCategory }: MemberComboboxProps) {
  const [open, setOpen] = useState(false);
  // Phase 23-08: accounts are the canonical billable party (members table dropped).
  const { data: allAccounts = [] } = useListAccounts({ status: "active" as const });
  const { data: plans = [] } = useListInventoryPlans({});
  const planCadenceById = new Map(plans.map((p) => [p.id, p.cadence]));

  // Filter by memberCategory if provided (Non_Recurring → look at membership plan cadence)
  // Since accounts don't directly carry planId, we show all active accounts when filtering.
  // Operators pick the account — the server validates the membership planId at booking time.
  const accounts: Account[] = memberCategory
    ? allAccounts.filter((a) => a.status === "active") // placeholder: all active accounts for now
    : allAccounts;

  void planCadenceById; // reserved for future plan-based filtering

  const selected = accounts.find((a) => a.id === value) ?? null;

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); onOpenChange?.(o); }}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          autoFocus={autoFocus}
          className={cn("w-full justify-between h-12 text-base", className)}
        >
          {selected ? (
            <span className="flex items-center gap-2">
              <span className="font-mono text-xs text-muted-foreground">{memId(selected.id)}</span>
              <span>{selected.legalName}</span>
            </span>
          ) : (
            <span className="text-muted-foreground">Search by name or ID…</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search by name or ID…" aria-label="Search accounts" />
          <CommandList>
            <CommandEmpty>No account matches your query.</CommandEmpty>
            <CommandGroup>
              {accounts.map((a) => (
                <CommandItem
                  key={a.id}
                  value={`${a.legalName} ${memId(a.id)} ${a.billingPhone ?? ""}`}
                  onSelect={() => { onChange(a.id); setOpen(false); }}
                  className="py-2"
                >
                  <div className="flex w-full flex-col gap-1">
                    <div className="flex w-full items-center justify-between">
                      <span className="text-sm font-medium">{a.legalName}</span>
                      <span className="font-mono text-xs text-muted-foreground">{memId(a.id)}</span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {a.billingPhone ?? a.billingEmail ?? "—"}
                    </div>
                  </div>
                  {value === a.id ? <Check className="ml-2 h-4 w-4" /> : null}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
